import { inject, injectable } from "inversify";
import { AjaxService, FormComponentService, FormService, TierSelectorService, UrlService, ValidationService } from ".";
import { IpfModule } from "..";
import { EventConstants } from "../EventConstants";
import { formatNumericInput } from "../functions/format-numeric-input";
import { getProperty } from "../functions/get-property";
import { isFormButton } from "../functions/is-form-button";
import { parseBoolean } from "../functions/parse-boolean";
import { setProperty } from "../functions/set-property";
import { TYPES } from "../ioc/types";
import { ApiUrl } from "../models/http";
import { ServiceBase } from "./ServiceBase";

/**
 * IPF form service class, it's purpose is to apply additional behaviours to form controls.
 * @see FormService
 */
@injectable()
export class IpfFormService extends ServiceBase implements FormService {

	/**
	 * Initialises a new instance of the IpfFormService class.
	 * @param ajaxService AJAX service, used to make AJAX HTTP requests.
	 * @param formComponentService Form component service, used to render React controls.
	 * @param tierSelectorService Tier selector service, used to apply tier selectors to input elements.
	 * @param urlService URL service, used to generate URLs.
	 * @param validationService Validation service, used to apply validation functionality to forms.
	 * @param ipfModule IPF module, containing contextual information and functions.
	 */
	public constructor(
		@inject(TYPES.AjaxService) private ajaxService: AjaxService,
		@inject(TYPES.FormComponentService) private formComponentService: FormComponentService,
		@inject(TYPES.TierSelectorService) private tierSelectorService: TierSelectorService,
		@inject(TYPES.UrlService) private urlService: UrlService,
		@inject(TYPES.ValidationService) private validationService: ValidationService,
		@inject(TYPES.IpfModule) private ipfModule: IpfModule
	) {
		super();
	}

	/**
	 * @inheritdoc
	 */
    public applyAll(target: Element): void {

        target.querySelectorAll("form").forEach((element: Element) => {

			const form = element as HTMLFormElement;

			this.applyPageHandlers(form);

            this.formComponentService.applyAll(form);

			this.tierSelectorService.applyInputTierSelectors(form);

			this.validationService.applyValidation(form);

			IpfFormService.applyRateAmountToggle(form);

			IpfFormService.applyFieldBehaviours(form);

			IpfFormService.applyNumericInputFormatting(form);
		});

    }

	/**
	 * @inheritdoc
	 */
    public applyPageHandlers(form: HTMLFormElement): void {

        const originalAction = form.action;

		// add a change event handler to the form, targeting elements with data-page-handler attributes
		$(form).off(EventConstants.change_ipf).on(EventConstants.change_ipf, "[data-page-handler]", (e) => {

            const element = e.currentTarget;

			// if input element
			if (element instanceof HTMLInputElement) {

				// exclude input with type=submit
				const input = element as HTMLInputElement;
				if (!isFormButton(input)) {

					const pageHandler = element.getAttribute("data-page-handler");

					if (pageHandler) {

						// on change get the page handler URL, set form action to it, then submit the form
						const handlerUrl = IpfFormService.getFormPageHandlerUrl(originalAction, pageHandler);
						form.action = handlerUrl;
						IpfFormService.submitForm(form, originalAction);
					}

					return;
				}
			}
			// else if select dropdown element
			else if (element instanceof HTMLSelectElement) {

				const pageHandler = element.getAttribute("data-page-handler");

				if (pageHandler) {

					// on change get the page handler URL, set form action to it, then submit the form
					const handlerUrl = IpfFormService.getFormPageHandlerUrl(originalAction, pageHandler);
					form.action = handlerUrl;
					IpfFormService.submitForm(form, originalAction);
				}
			}
        });

		// add a click event handler to the form, targeting elements with data-page-handler attributes, buttons with a formaction, but not dropdowns
		$(form).off(EventConstants.click_ipf).on(EventConstants.click_ipf, "[data-page-handler]:not(select), button[formaction]", e => {

            const element = e.currentTarget;

            if (element instanceof HTMLElement) {

				const pageHandler = element.getAttribute("data-page-handler") ?? "";
				const isSubmitButton = element.tagName === "BUTTON" && element.getAttribute("type") !== "button";

				// if this is a page handler or a button with type=submit
				if (pageHandler || isSubmitButton) {

					// get the handler URL
					const handlerUrl = IpfFormService.getFormPageHandlerUrl(originalAction, pageHandler);
					const isNonSubmitButton = element.tagName === "BUTTON" && element.getAttribute("type") === "button";

					// if it's a button with type=button need to set formAction attribute
					if (isNonSubmitButton) {

						const btn = element as HTMLButtonElement;
						btn.formAction = handlerUrl;
					}

					// ensure the active last focused element is known
					this.setEnsuredActiveElement(form, element);

					const isLinkButton = element.tagName === "A";
					const isDiv = element.tagName === "DIV";

					// if it's a link button, div, or button with type=button
					// set form action and submit the form
					if (isNonSubmitButton || isLinkButton || isDiv) {

						form.action = handlerUrl;
						IpfFormService.submitForm(form, originalAction);
						e.stopPropagation();
                    }
                }
            }
        });
    }

	/**
	 * @inheritdoc
	 */
    public applyApiHandlers(container: HTMLElement, successCallback?: (response: any) => void): void {

		// add a change event handler to the form, targeting elements with data-api-handler attributes
		$(container).off(EventConstants.change_ipf).on(EventConstants.change_ipf, "[data-api-handler]", e => {

            const element = e.currentTarget;

			// if input element
            if (element instanceof HTMLInputElement) {

                const handlerString = element.getAttribute("data-api-handler");

				// check this is not a button and there is a handler string
				if (!isFormButton(element) && handlerString) {

					// JSON parse the API URL call details
                    const apiHandler = JSON.parse(handlerString) as ApiUrl;

                    if (apiHandler) {

						// now perform the AJAX request
						this.ajaxService.request({
							url: this.urlService.apiUrl(apiHandler),
                            method: apiHandler.method || "POST",
                            data: apiHandler.data
                        })
                            .then((response: any) => {

								// call the callback if supplied and pass it the response
                                if (successCallback) {

                                    successCallback(response);
                                }
                            });
                    }
                    return;
                }
            }
        });

		// add a click event handler to the form, targeting elements with data-api-handler attributes
        $(container).off("click.ipfform").on("click.ipfform", "[data-api-handler]", e => {

            const element = e.currentTarget as HTMLElement;
            const handlerString = element.getAttribute("data-api-handler");

            if (handlerString) {

					// JSON parse the API URL call details
                const apiHandler = JSON.parse(handlerString) as ApiUrl;

                if (apiHandler) {

					// now perform the AJAX request
					this.ajaxService.request({
						url: this.urlService.apiUrl(apiHandler),
                        method: apiHandler.method || "POST",
                        data: apiHandler.data || {}
                    })
                        .then((response: any) => {

							// call the callback if supplied and pass it the response
                            if (successCallback) {

                                successCallback(response);
                            }
                        });
                }
            }
        });
    }

	/**
	 * @inheritdoc
	 */
	public bindModel<TBindType>(options: { element: Element | JQuery<HTMLElement>, model: TBindType, onChange?: (field: string) => void }): TBindType {

        const modelAny = options.model as any;

		// add a change event handler to the form
		$(options.element).off(EventConstants.change_ipf).on(EventConstants.change_ipf, "input:not([data-bind=false]):not([data-bind-event=oninput]),select:not([data-bind=false])", (event) => {

            const field = $(event.currentTarget);

			// set the model value to that extracted from the field
            IpfFormService.setFieldValue(modelAny, field);

			// if an onchange was provided, call it and pass name of the changed field
            if (options.onChange) {

				const dataBind = field.data("bind");
				const name = typeof dataBind === "string" ? dataBind : field.attr("name") as string;
                options.onChange(name);
            }
		});

		// add an input event handler to the form
		$(options.element).off(EventConstants.input_ipf).on(EventConstants.input_ipf, "input[data-bind-event=oninput]", (event) => {

			const field = $(event.currentTarget);

			// set the model value to that extracted from the field
			IpfFormService.setFieldValue(modelAny, field);

			// if an onchange was provided, call it and pass name of the changed field
			if (options.onChange) {

				const dataBind = field.data("bind");
				const name = typeof dataBind === "string" ? dataBind : field.attr("name") as string;
				options.onChange(name);
			}
		});

		// now loop all input and select elements and extract values to set the initial model values
        $(options.element).find("input,select").not("[data-bind=false]").each((index: number, element: Element) => {

            const field = $(element);
            const dataBind = field.data("bind");
            const path = typeof dataBind === "string" ? dataBind : field.attr("name") as string;

            if (path && path.indexOf(".") > -1) {

                const lastIndex = path.lastIndexOf('.');
                const parentName = path.substring(0, lastIndex);
                const items = parentName.split('.');

                let inScope: any = modelAny;

				// loop through path segments and setup container objects (where not set) for the whole property path
                for (let i = 0; i < items.length; i++) {

                    const name = items[i];
                    const prop = getProperty(inScope, name);

                    if (!prop) {

                        let obj = {};
                        inScope[name] = obj;
                        inScope = obj;
                    }
                    else {

                        inScope = prop;
                    }
                }
            }

			// now set the field value
            IpfFormService.setFieldValue(modelAny, field);
        });

        return options.model;
    }

	/**
	 * Submits form programatically by dispatching the submit event on the target form.
	 * @param form The target form element to submit.
	 * @param originalAction The action attribute to reinstate after submitting.
	 */
	private static submitForm(form: HTMLFormElement, originalAction: string): void {

        if (form.hasAttribute("data-ajax")) {

			const event = new Event("submit", {
				cancelable: true
			});

			form.dispatchEvent(event);
        }
        else {

            form.submit();
        }

        form.action = originalAction;
    }

	/**
	 * Gets the form page handler URL by joining form action and a formatted page handler.
	 * @param formAction The form action.
	 * @param pageHandlerString The page handler.
	 */
    private static getFormPageHandlerUrl(formAction: string, pageHandlerString: string): string {

		// get any URL query string
        let query: string;
        let queryIndex: number;
        if ((queryIndex = formAction.indexOf("?")) > -1) {

            query = formAction.substring(queryIndex);
            formAction = formAction.substring(0, queryIndex);
        }
        else {

            query = "";
        }

		// get any URL hash string
        let hash: string;
        let hashIndex: number;
        if ((hashIndex = formAction.indexOf("#")) > -1) {

            hash = formAction.substring(hashIndex);
            formAction = formAction.substring(0, hashIndex);
        }
        else {

            hash = "";
        }

        const testPageHandlerString = pageHandlerString.toLowerCase();

		let pageHandler: string;

		// test for on[verb] prefix, if found remove it as the server requires it without to route correctly
		if (testPageHandlerString.startsWith("onpost")) {

			pageHandler = pageHandlerString.substring(6);
		}
		else if (testPageHandlerString.startsWith("onget")) {

            pageHandler = pageHandlerString.substring(5);
        }
		else if (testPageHandlerString.startsWith("onput")) {

            pageHandler = pageHandlerString.substring(5);
        }
		else if (testPageHandlerString.startsWith("ondelete")) {

            pageHandler = pageHandlerString.substring(8);
        }
        else {

			pageHandler = pageHandlerString;
		}

        let action = [formAction, pageHandler].join("/");

        if (query) {

            action += query;
        }

        if (hash) {

            action += hash;
        }

		return action;
    }

	/**
	 * Sets the value of a model field from its corresponding element.
	 * @param model The model.
	 * @param field The field element, must have a name attribute to map the value to.
	 */
    private static setFieldValue(model: any, field: JQuery<Element>) {

		const dataBind = field.data("bind");

		// extract the field name from the name attribute
        const name = typeof dataBind === "string" ? dataBind : field.attr("name") as string;

        if (!name) {

            return;
        }

		// if radio button only extract if checked
		if (field.attr("type") === "radio") {

            if (field.prop("checked") === true) {

				setProperty(model, name, IpfFormService.extractFieldValue(field));
            }
		}
		// if checkbox
        else if (field.attr("type") === "checkbox") {

            setProperty(model, name, field.prop("checked"));
        }
        else {

			setProperty(model, name, IpfFormService.extractFieldValue(field));
        }
	}

	/**
	 * Extracts the field value from an element.
	 * @param field The field element.
	 * @returns Date | Number | Boolean | String.
	 */
	private static extractFieldValue(field: JQuery<Element>) {

		let stringValue = field.val()?.toString() ?? "";

		// if the field has numeric formatting applied, strip all chars except digits and dot
		if (field.is("[data-numeric-format]")) {

			stringValue = stringValue.replace(/[^0-9\.]+/g, "");
		}

		const fieldType = field.data("bind-type");
		const isString = fieldType?.toLowerCase() === "string";
		let numberValue: number;
		let booleanValue: boolean | null;

		if (field.attr("type") === "datetime-local") {
			return stringValue ? new Date(stringValue) : stringValue;
		}
		
		// If explicitly marked as string type, return as string
		if (isString) {
			return stringValue;
		}

		// Try parsing as number
		if (stringValue !== "") {
			numberValue = parseFloat(stringValue);
			if (!isNaN(numberValue)) {
				return numberValue;
			}
		}

		// Try parsing as boolean
		booleanValue = parseBoolean(stringValue);
		if (typeof booleanValue === "boolean") {
			return booleanValue;
		}

		// Default to string if all else fails
		return stringValue;
	}

	/**
	 * Apply toggle behaviour to fields that allow switching between a rate value and an amount.
	 * @param form The ancestor form element.
	 */
    private static applyRateAmountToggle(form: HTMLFormElement) {

        const toggle = (input: HTMLInputElement, rateField: JQuery<Element>, amountField: JQuery<Element>, autoFocus: boolean) => {

            if(input.checked === true) {
				amountField.css("visibility", "visible");
				amountField.css("height", "auto");

                if (autoFocus) amountField.find("input").focus();

				rateField.css("visibility", "hidden");
				rateField.css("height", "0px");
            }
            else {
				rateField.css("visibility", "visible");
				rateField.css("height", "auto");

                if (autoFocus) rateField.find("input").focus();

				amountField.css("visibility", "hidden");
				amountField.css("height", "0px");
            }
        }

		// find all relevant toggle fields
        form.querySelectorAll("input[data-toggle-rate-amount]").forEach((element: Element) => {

			const input = element as HTMLInputElement;
			const jinput = $(element);
			const fieldWrap = jinput.closest(".field-wrap");
            const rateField = fieldWrap.find(".rate-field");
            const amountField = fieldWrap.find(".amount-field");

            toggle(input, rateField, amountField, false);

			// toggle the display to switch between rate and amount
			input.addEventListener(EventConstants.change, () => {

				toggle(input, rateField, amountField, true);
				jinput.trigger(EventConstants.change_ipf);
            });
        });
	}

	/**
	 * Applies various field behaviours to elements within a <form> element.
	 * @param form The <form> element.
	 */
	private static applyFieldBehaviours(form: HTMLFormElement) {

		const jForm = $(form);
		const trims = jForm.find("[data-trim]");

		// trim text boxes with data-trim attribute on change
		trims.off(EventConstants.blur_ipf).on(EventConstants.blur_ipf, event => {

			const textBox = $(event.currentTarget);
			let value = textBox.val()?.toString();

			if (value) {

				// to trim emails remove the text first otherwise it doesn't change the visible text
				if (textBox.attr("type") === "email") {
					textBox.val("");
				}
				textBox.val(value.trim());
			}
		});

		// trim text boxes with data-trim attribute on paste
		trims.off(EventConstants.paste_ipf).on(EventConstants.paste_ipf, event => {

			const textBox = $(event.currentTarget);
			const clipboard = (event.originalEvent as any).clipboardData;
			let value = clipboard?.getData("text");

			if (value) {

				textBox.val(value.trim());
				event.preventDefault();
			}
		});
	}

	/**
	 * Applies numeric input formatting to <input> elements within a <form> element.
	 * @param form The <form> element.
	 */
	private static applyNumericInputFormatting(form: HTMLFormElement) {

		// get all inputs with the data-numeric-format attribute
		const inputs = form.querySelectorAll<HTMLInputElement>("[data-numeric-format]");

		// apply formatting now
		inputs.forEach(input =>
			formatNumericInput(input)
		);

		// add blur event
		$(inputs).off(EventConstants.blur_ipfnumericformat).on(EventConstants.blur_ipfnumericformat, event =>
			formatNumericInput(event.currentTarget as HTMLInputElement)
		);

		// add input event
		$(inputs).off(EventConstants.input_ipfnumericformat).on(EventConstants.input_ipfnumericformat, event => 
			formatNumericInput(event.currentTarget as HTMLInputElement, true)
		);

		// clear numeric value on focus if it is equal to 0
		$(inputs).focus((event) => {
			const input = $(event.currentTarget);
			const value = input.val();
			const numValue = value ? parseFloat(value.toString()) : NaN;
			if (!isNaN(numValue) && numValue === 0) {
				input.val("");
			}
		});
	}

	/**
	 * Sets the active form element global variable,
	 * used to identity which element was last focused when submitting an AJAX form.
	 * This enables button level form actions to be extracted, whilst catering for browser bugs in Safari.
	 * @param form The target form.
	 * @param element The focused element.
	 */
	private setEnsuredActiveElement(form: HTMLFormElement, element: HTMLElement) {

		const activeElement = document.activeElement;

		// safari workaround for SubmitEvent.submitter and document.activeElement=<body> browser bugs:
		// is an AJAX form then set the ipfModule.ensuredActiveElement for use in AjaxService implementation
		// only set ipfModule.ensuredActiveElement when the activeElement isn't set or is not an input/button/select element
		if ((!activeElement ||
			activeElement.tagName === document.body.tagName ||
			["INPUT", "BUTTON", "SELECT"].indexOf(activeElement.tagName) === -1) &&
			form.hasAttribute("data-ajax")) {
			this.ipfModule.ensuredActiveElement = element;
		}
	}
}
